/* global userpilot */
import axios, { userApi } from '@atheneum/api'
import { tokenStorage } from '@atheneum/api/dist/lib'
import find from 'lodash/find'
import get from 'lodash/get'
import { action, computed, extendObservable } from 'mobx'

class UserStore {
  constructor(socket, staticData, notificationStore) {
    extendObservable(this, {
      loading: true,
      user: null,
      error: null,
      staticData: staticData,
      socket: null,
      notificationStore,
      teamId: computed(() => get(this.user, 'currentPosition.teamId'))
    })

    this.socket = socket
    this.socket.on('connect', () => {
      if (this.user) {
        this.socket.emit('socketAuth', tokenStorage.get())
      }
    })
    this.loadUser()
  }

  addHistory = (history) => {
    this.history = history
  }

  loadUser() {
    const token = tokenStorage.get()
    if (token) {
      return this.quickLogin(token)
    } else {
      this.loading = false
    }
  }

  login(data) {
    this.loading = true

    return userApi.login(data)
      .then(({ token, user }) => {
        axios.defaults.headers.Authorization = token
        this.handleLoginSuccess(user, token)
        this.loading = false
      })
      .catch(err => {
        this.error = err.message
        this.handleInvalidToken(err)
        this.loading = false
        throw err
      })
  }

  quickLogin(token) {
    this.loading = true

    return userApi.quickLogin(token)
      .then(({ user }) => {
        if (!this.user || get(this.user, 'id') !== user.id) {
          axios.defaults.headers.Authorization = token
        }
        this.handleLoginSuccess(user, token)
        this.loading = false
      })
      .catch(err => {
        this.error = err ? err.message : 'Unknown error occured'
        this.handleInvalidToken(err)
        this.loading = false
        throw err
      })
  }

  logout = action(() => {
    this.history.push('/logged-out')
    tokenStorage.clear()
    this.user = null
    delete axios.defaults.headers.Authorization
    this.socket.disconnect()
  })

  handleInvalidToken(error) {
    if (
      error &&
      error.statusCode === 401 &&
      error.message && error.message.includes('Invalid token')
    ) {
      tokenStorage.clear()
    }
  }

  handleLoginSuccess(user, token) {
    tokenStorage.save(token, user)
    this.user = user
    this.connectSocket()
    this.notificationStore.getNotificationCount()

    if (userpilot && this.user) {
      const env = process.env.REACT_APP_ENV || 'unknown'

      if (process.env.REACT_APP_ENV !== 'local') {
        userpilot.identify(`${env}_${this.user.id}`, {
          email: this.user.email,
          userTypeId: this.user.userTypeId,
          env: env,
        })
      }
    }
  }

  forgotPassword(data) {
    return userApi.forgotPassword(data).catch(err => {
      this.error = err.message
      throw err
    })
  }

  resetPassword(data) {
    return userApi.resetPassword(data).catch(err => {
      this.error = err.message
      throw err
    })
  }

  connectSocket() {
    this.socket.emit('socketAuth', tokenStorage.get())
    this.notificationStore.notificationListener(this.socket, this.user)
  }

  /**
   * Check if user has a certain permission
   *
   * @param {number|string} permission ID or permission name
   * @returns {boolean} does user have passed permission
   */
  hasPermission(permission) {
    if (typeof permission === 'number') {
      return !!find(this.user.permissions, { id: permission })
    } else {
      return !!find(this.user.permissions, { name: permission })
    }
  }

  /**
   * Check if user has any of passed employee positions
   *
   * @param {string[]} employee positions array
   * @returns {boolean} does user have any of passed employee positions
   */
  hasAnyOfEmployeePositions(positions) {
    const employeePosition = this.user.currentPosition.employeePositionId
    return positions.indexOf(employeePosition) !== -1
  }

  isAdmin() {
    const ADMIN = this.staticData.CONST.PERMISSIONS.ADMIN
    return this.hasPermission(ADMIN)
  }

  isAccounting() {
    const ACCOUNTING = this.staticData.CONST.PERMISSIONS.ACCOUNTING
    return this.hasPermission(ACCOUNTING)
  }

  isAdvancedReporting() {
    const ADVANCED_REPORTING = this.staticData.CONST.PERMISSIONS
      .ADVANCED_REPORTING
    return this.hasPermission(ADVANCED_REPORTING)
  }
}

export default UserStore
